import { createContext, useContext, useState, useCallback } from 'react';
import paquetesApi from 'legacyfrontend/api/paquetes';
import {
  getCachedData,
  updateCacheIfChanged,
  removeCachedData,
  getCacheInfo,
} from 'legacyfrontend/helpers/cacheHelper';

const CACHE_CONFIG = {
  packageSalidas: {
    keyPrefix: 'package_salidas',
    ttl: 5 * 60 * 1000,
    enabled: true,
    backgroundRefresh: true,
  },
  packageData: {
    keyPrefix: 'package_data',
    ttl: 10 * 60 * 1000,
    enabled: true,
    backgroundRefresh: true,
  },
};

const defaultPackageCacheValues = {
  fetchPackageSalidas: () => Promise.resolve([]),
  fetchPackageData: () => Promise.resolve(null),
  clearPackageSalidasCache: () => {},
  clearPackageDataCache: () => {},
  clearAllPackageCache: () => {},
  getPackageCacheStatus: () => null,
};

const PackageCacheContext = createContext(defaultPackageCacheValues);

export const PackageCacheProvider = ({ children }) => {
  /**
   * Fetch salidas disponibles para un paquete con caché
   * @param {string|number} packageId - ID del paquete
   * @param {string} monthYear - Formato "MM-YYYY"
   * @param {string} token - Token de autenticación
   * @param {number|null} adultsQty - Cantidad de adultos (opcional)
   * @param {boolean} forceRefresh - Forzar recarga sin caché
   * @returns {Promise<Array>} Array de salidas
   */
  const fetchPackageSalidas = useCallback(async (packageId, monthYear, token, adultsQty = null, forceRefresh = false) => {
    if (!packageId || !monthYear || !token) {
      return [];
    }

    const config = CACHE_CONFIG.packageSalidas;
    const adultsKey = adultsQty ? `_${adultsQty}` : '';
    const cacheKey = `${config.keyPrefix}_${packageId}_${monthYear}${adultsKey}`;
    
    if (!config.enabled && !forceRefresh) {
      const response = await paquetesApi.getInternalPackageSalidas(packageId, monthYear, token, adultsQty);
      return response || [];
    }

    try {
      if (!forceRefresh) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          if (config.backgroundRefresh) {
            setTimeout(async () => {
              try {
                const response = await paquetesApi.getInternalPackageSalidas(packageId, monthYear, token, adultsQty);
                updateCacheIfChanged(cacheKey, response || [], config.ttl);
              } catch (error) {
                console.warn('Error en background refresh de salidas:', error);
              }
            }, 0);
          }
          
          return cachedData;
        }
      }

      const response = await paquetesApi.getInternalPackageSalidas(packageId, monthYear, token, adultsQty);
      const salidas = response || [];
      
      const { data } = updateCacheIfChanged(cacheKey, salidas, config.ttl);
      
      return data;
      
    } catch (error) {
      console.error('Error fetching package salidas:', error);
      
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        return cachedData;
      }
      
      throw error;
    }
  }, []);

  /**
   * Fetch datos completos de un paquete con caché
   * @param {string|number} packageId - ID del paquete
   * @param {string} token - Token de autenticación
   * @param {string|number} salidaId - ID de la salida (opcional)
   * @param {boolean} forceRefresh - Forzar recarga sin caché
   * @returns {Promise<Object>} Datos del paquete
   */
  const fetchPackageData = useCallback(async (packageId, token, salidaId = null, forceRefresh = false) => {
    if (!packageId || !token) {
      return null;
    }

    const config = CACHE_CONFIG.packageData;
    const cacheKey = salidaId 
      ? `${config.keyPrefix}_${packageId}_${salidaId}`
      : `${config.keyPrefix}_${packageId}`;
    
    if (!config.enabled && !forceRefresh) {
      const response = await paquetesApi.getInternalPackage(packageId, token, salidaId);
      return response;
    }

    try {
      if (!forceRefresh) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          if (config.backgroundRefresh) {
            setTimeout(async () => {
              try {
                const response = await paquetesApi.getInternalPackage(packageId, token, salidaId);
                updateCacheIfChanged(cacheKey, response, config.ttl);
              } catch (error) {
                console.warn('Error en background refresh de paquete:', error);
              }
            }, 0);
          }
          
          return cachedData;
        }
      }

      const response = await paquetesApi.getInternalPackage(packageId, token, salidaId);
      
      const { data } = updateCacheIfChanged(cacheKey, response, config.ttl);
      
      return data;
      
    } catch (error) {
      console.error('Error fetching package data:', error);
      
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        return cachedData;
      }
      
      throw error;
    }
  }, []);

  /**
   * Limpia el caché de salidas de un paquete específico
   */
  const clearPackageSalidasCache = useCallback((packageId, monthYear) => {
    const config = CACHE_CONFIG.packageSalidas;
    const cacheKey = `${config.keyPrefix}_${packageId}_${monthYear}`;
    removeCachedData(cacheKey);
  }, []);

  /**
   * Limpia el caché de datos de un paquete específico
   */
  const clearPackageDataCache = useCallback((packageId, salidaId = null) => {
    const config = CACHE_CONFIG.packageData;
    const cacheKey = salidaId 
      ? `${config.keyPrefix}_${packageId}_${salidaId}`
      : `${config.keyPrefix}_${packageId}`;
    removeCachedData(cacheKey);
  }, []);

  /**
   * Limpia todo el caché de paquetes
   */
  const clearAllPackageCache = useCallback(() => {
  }, []);

  /**
   * Obtiene el estado del caché de un recurso específico
   */
  const getPackageCacheStatus = useCallback((type, packageId, identifier) => {
    const config = CACHE_CONFIG[type];
    if (!config) return null;
    
    const cacheKey = identifier 
      ? `${config.keyPrefix}_${packageId}_${identifier}`
      : `${config.keyPrefix}_${packageId}`;
    
    return getCacheInfo(cacheKey);
  }, []);

  const values = {
    fetchPackageSalidas,
    fetchPackageData,
    clearPackageSalidasCache,
    clearPackageDataCache,
    clearAllPackageCache,
    getPackageCacheStatus,
  };

  return (
    <PackageCacheContext.Provider value={values}>
      {children}
    </PackageCacheContext.Provider>
  );
};

export const usePackageCache = () => {
  const context = useContext(PackageCacheContext);
  
  if (!context) {
    throw new Error('usePackageCache must be used within PackageCacheProvider');
  }
  
  return context;
};

export default PackageCacheContext;

